/**
 * AnalysisLogsStream.jsx — Live and historical log viewer for an analysis run.
 *
 * WebSocket lifecycle:
 *   1. On mount, the component checks the analysis status via GET /analysis/:id/status.
 *   2. If the analysis is "running" or "pending", a WebSocket connection is opened to
 *      /api/v1/logs/stream/:analysisId. The protocol (ws:// vs wss://) is inferred from
 *      the current page protocol to work in both HTTP dev and HTTPS prod environments.
 *   3. Each incoming WebSocket message is appended to the logs array and displayed.
 *      If the message indicates the container was not found, the component falls back to
 *      fetching historical logs instead of continuing the live stream.
 *   4. If the WebSocket connection itself fails (onerror), the component falls back to
 *      fetching historical logs via GET /logs/:id/historical.
 *   5. For completed/failed analyses (status is not running or pending), historical logs
 *      are fetched directly without attempting a WebSocket connection.
 *   6. On unmount, the WebSocket is closed to prevent memory leaks.
 *
 * The UI shows a status badge (Loading / Live / Historical / Error) based on the
 * current connection state, and auto-scrolls to the bottom as new log lines arrive.
 *
 * @param {string|number} props.analysisId - The analysis ID to stream logs for.
 */
import { useEffect, useState, useRef } from 'react';
import { analysisAPI, logAPI } from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { FileText, Radio, Clock, AlertCircle, Loader2 } from 'lucide-react';

function AnalysisLogsStream({ analysisId }) {
    // logs: array of individual log line strings received over the WebSocket.
    const [logs, setLogs] = useState([]);
    // error: human-readable error message to display in the UI, or null if no error.
    const [error, setError] = useState(null);
    // connectionStatus: drives the status badge. Possible values:
    //   'loading' | 'loading_historical' | 'connecting' | 'live' | 'historical' | 'error' | 'closed'
    const [connectionStatus, setConnectionStatus] = useState('loading');
    // historicalLogs: raw log text fetched from the REST fallback endpoint, or null
    // when the component is using the live WebSocket stream.
    const [historicalLogs, setHistoricalLogs] = useState(null);
    // isLive: true while a WebSocket connection is open and receiving data.
    const [isLive, setIsLive] = useState(false);
    // logsEndRef: attached to an empty div at the bottom of the live log list
    // so we can scroll it into view whenever new lines arrive.
    const logsEndRef = useRef(null);

    // Auto-scroll to the bottom whenever new live log lines are added.
    useEffect(() => {
        logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [logs]);

    // Auto-scroll to the bottom when historical logs finish loading.
    useEffect(() => {
        if (historicalLogs) {
            logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [historicalLogs]);

    // Main effect: determine whether to open a WebSocket or fetch historical logs,
    // then manage the connection lifecycle. Runs once per analysisId change.
    useEffect(() => {
        let ws = null;
        let fallbackToHistorical = false;

        /**
         * Fetches completed log output from the REST endpoint.
         * Used when the analysis is already finished or when the WebSocket fails.
         */
        const fetchHistoricalLogs = async (phase = null) => {
            setConnectionStatus('loading_historical');
            try {
                const response = await logAPI.getHistoricalLogs(analysisId, phase);
                setHistoricalLogs(response.data.logs);
                setConnectionStatus('historical');
                setIsLive(false);
            } catch (error) {
                setError(error.response?.data?.detail || error.message);
                setConnectionStatus('error');
            }
        };

        /**
         * Opens a WebSocket connection to the live log stream endpoint.
         * Falls back to historical logs if the connection errors or if the
         * backend signals that the container no longer exists.
         */
        const connectWebSocket = () => {
            setConnectionStatus('connecting');
            // Use dynamic URL based on environment
            const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const wsHost = window.location.host;
            ws = new WebSocket(`${wsProtocol}//${wsHost}/api/v1/logs/stream/${analysisId}`);

            // Connection established — switch UI to live mode.
            ws.onopen = () => {
                setConnectionStatus('live');
                setIsLive(true);
            };

            // Each message is a single log line. If the backend signals that the
            // container is gone, fall back to historical instead of streaming further.
            ws.onmessage = (event) => {
                if (event.data.includes('container') && event.data.includes('not found')) {
                    fallbackToHistorical = true;
                    fetchHistoricalLogs();
                } else {
                    setLogs(prevLogs => [...prevLogs, event.data]);
                }
            };

            // Connection failed — show the error and attempt historical fallback once.
            ws.onerror = () => {
                setError("WebSocket connection failed");
                setConnectionStatus('error');
                if (!fallbackToHistorical) {
                    fallbackToHistorical = true;
                    fetchHistoricalLogs();
                }
            };

            // Connection closed by the server — update the badge if we were live.
            ws.onclose = () => {
                if (isLive) {
                    setConnectionStatus('closed');
                }
            };
        };

        /**
         * Entry point: checks the analysis status and decides whether to open
         * a live WebSocket or go straight to historical logs.
         */
        const init = async () => {
            try {
                const response = await analysisAPI.getStatus(analysisId);
                const status = response.data.analysis_status;
                const phase = response.data.phase || null;
                if (status === "running" || status === "pending") {
                    connectWebSocket();
                } else {
                    fetchHistoricalLogs(phase);
                }
            } catch (error) {
                setError("Failed to get analysis status");
                setConnectionStatus('error');
            }
        };

        init();

        // Cleanup: close the WebSocket when the component unmounts or analysisId changes
        // to prevent dangling connections and stale state updates.
        return () => {
            if (ws) ws.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analysisId]);

    /**
     * Returns the status badge element that corresponds to the current connectionStatus.
     * @returns {JSX.Element|null}
     */
    const getStatusDisplay = () => {
        switch (connectionStatus) {
            case 'loading':
            case 'loading_historical':
            case 'connecting':
                return (
                    <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading...
                    </span>
                );
            case 'live':
                return (
                    <span className="flex items-center gap-1.5 text-green-600 dark:text-green-500 text-sm">
                        <Radio className="h-3 w-3" />
                        Live
                    </span>
                );
            case 'historical':
                return (
                    <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
                        <Clock className="h-3 w-3" />
                        Historical
                    </span>
                );
            case 'error':
                return (
                    <span className="flex items-center gap-1.5 text-destructive text-sm">
                        <AlertCircle className="h-3 w-3" />
                        Error
                    </span>
                );
            default:
                return null;
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Logs
                    </CardTitle>
                    {getStatusDisplay()}
                </div>
            </CardHeader>
            <CardContent>
                {error && (
                    <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
                        {error}
                    </div>
                )}

                <div className="bg-muted/50 rounded-lg p-4 h-[800px] overflow-y-auto">
                    {historicalLogs ? (
                        // Historical mode: render the entire log blob as a single <pre> block.
                        // logsEndRef sits after the block so the scroll anchor works here too.
                        <>
                            <pre className="text-sm whitespace-pre-wrap break-words font-mono">
                                {historicalLogs}
                            </pre>
                            <div ref={logsEndRef} />
                        </>
                    ) : logs.length > 0 ? (
                        // Live mode: render each log line as a separate div and keep the
                        // scroll anchor at the end so new lines stay visible.
                        <div className="space-y-1 font-mono text-sm">
                            {logs.map((log, index) => (
                                <div key={index}>{log}</div>
                            ))}
                            <div ref={logsEndRef} />
                        </div>
                    ) : (
                        // No logs yet — show a contextual placeholder message.
                        <div className="h-full flex items-center justify-center text-muted-foreground">
                            {connectionStatus === 'loading' || connectionStatus === 'connecting'
                                ? 'Waiting for logs...'
                                : 'No logs available'
                            }
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

export default AnalysisLogsStream;
